import React from 'react';
import InnerBlock from '../../../../shared/web/layout/InnerBlock';
import { useParams } from 'react-router-dom';
import { useDataLoaderNew } from 'data-loader';
import { MARKETS_UPDATED, SetupArea, SetupAreaVersion, useSetupAreasAdminService } from 'nekst-api';
import {
  Header4,
  List,
  LoadingAnimation,
  SimpleBlock,
  TextLink,
  usePopupManager,
  useShowContextRequired
} from 'ui-builder';
import { ClickableBlock } from 'features/tasks-forms';
import styles from '../Markets.module.scss';
import {
  IconButton,
  IconType,
  InformationMessage,
  RowRepeater,
  TimezoneDisplay,
  usePopupFormManager
} from 'features/nekst-widgets';
import useSettingsNavigator from '../../../settingsNavigator';
import { SetupAreaBreadcrumbs } from '../SetupAreaBreadcrumbs';
import { SetupAreaVersionStatusWidget } from './SetupAreaVersionStatusWidget';
import { SetupAreaActionsButton } from './SetupAreaActionsButton';
import { UpdateVersionFormButton } from './UpdateVersionForm';
import { DeleteSetupAreaVersionForm } from './DeleteSetupAreaVersionForm';
import { CreateVersionForm } from './CreateVersionForm';

function DescriptionBlock(props: {
  setupArea: SetupArea
}) {
  const setupArea = props.setupArea;

  if (setupArea.market?.description) {
    return (<InformationMessage>{setupArea.market?.description}</InformationMessage>);
  } else {
    return null;
  }
}

function SetupAreaVersionDeleteIconButton(props: {
  setupAreaId: number,
}) {

  const showContext = useShowContextRequired<SetupAreaVersion>();

  const popupManager = usePopupManager();

  if (showContext.data.numberOfUsages === 0) {
    return (
      <IconButton
        onClick={() => {
          popupManager.openPopup(<DeleteSetupAreaVersionForm
            setupAreaId={props.setupAreaId}
            setupAreaVersion={showContext.data.version}
          />);
        }}
        type={IconType.TRASH_CAN}
      />
    );
  } else {
    return null;
  }
}

function SetupAreaCopyIconButton(props: {
  setupArea: SetupArea,
}) {
  const showContext = useShowContextRequired<SetupAreaVersion>();

  const popupFormManager = usePopupFormManager();

  return (
    <IconButton
      onClick={() => {
        popupFormManager.openForm(
          <CreateVersionForm
            setupArea={props.setupArea}
            initialSourceVersion={showContext.data?.version}
          />
        );
      }}
      type={IconType.COPY}
    />
  );
}

function SetupAreaVersionRow(props: {
  state: string | undefined,
  setupArea: SetupArea,
}) {
  const showContext = useShowContextRequired<SetupAreaVersion>();

  const { data } = showContext;

  const settingsNavigator = useSettingsNavigator();

  const getTitle = () => {
    return data.version;
  };

  const version = data.version;

  return (
    <ClickableBlock
      title={(
        <>
          {getTitle()} ({data.numberOfUsages})
          <SetupAreaVersionStatusWidget setupAreaVersion={data} />
        </>
      )}
      onClick={() => {
        settingsNavigator.openAdminSetupAreaVersionPage(
          props.state,
          props.setupArea.id,
          version
        );
      }}
      buttons={(
        !props.setupArea.isDefault && (
          <>
            <SetupAreaCopyIconButton setupArea={props.setupArea} />
            <SetupAreaVersionDeleteIconButton setupAreaId={props.setupArea.id} />
            <UpdateVersionFormButton setupAreaId={props.setupArea.id} />
          </>
        )
      )}
      content={(
        <SimpleBlock className={styles.links}>
          <TextLink
            onClick={() => {
              settingsNavigator.openAdminMarketDetailsFieldsPage(
                props.state,
                props.setupArea.id,
                version
              );
            }}
          >
            Detail Boxes
            {` (${data.numberOfDetailFields})`}
          </TextLink>
          <TextLink
            onClick={async () => {
              settingsNavigator.openAdminSetupAreaPlansPage(
                props.state,
                props.setupArea.id,
                data.version
              );
            }}
          >
            Workflows
            {` (${data.numberOfPlans})`}
          </TextLink>
          <TextLink
            href={settingsNavigator.getAdminMarketAiTrainingLink(props.state, props.setupArea.id, version)}
            onClick={async () => {
              settingsNavigator.openAdminMarketAiTrainingPage(props.state, props.setupArea.id, version);
            }}
          >
            AI Training
          </TextLink>
        </SimpleBlock>
      )}
    />
  );
}

export default function SetupAreaPage() {

  const { setupAreaId: setupAreaIdString } = useParams<{ setupAreaId: string }>();

  const setupAreaId = parseInt(setupAreaIdString!, 10);

  const setupAreasAdminService = useSetupAreasAdminService();

  const { data: setupArea } = useDataLoaderNew(
    async () => setupAreasAdminService.getSetupAreaById(setupAreaId),
    {
      events: [MARKETS_UPDATED],
      dependencies: [setupAreaId]
    }
  );

  if (setupArea) {
    return (
      <>
        <SetupAreaBreadcrumbs setupArea={setupArea} />

        <InnerBlock
          title={setupArea.name}
          titleLine2={setupArea.timezone ? <TimezoneDisplay value={setupArea.timezone} label="Timezone" /> : undefined}
          headerRight={<SetupAreaActionsButton setupArea={setupArea} />}
        >
          <DescriptionBlock setupArea={setupArea} />
          <Header4>Versions:</Header4>
          <List
            getDataFunc={async () => setupArea.versions}
            dependencies={[JSON.stringify(setupArea.versions)]}
          >
            <RowRepeater>
              <SetupAreaVersionRow state={setupArea.market?.state} setupArea={setupArea} />
            </RowRepeater>
          </List>
        </InnerBlock>
      </>
    );
  } else {
    return (
      <LoadingAnimation />
    );
  }
}
